import React, { useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn, Validate } from 'react-hook-form';
import { useDebounce } from 'use-debounce';

import { UsersApi } from 'api';
import { ReactComponent as LocationIcon } from 'assets/images/ic-dispensary-location.svg';
import type { AutocompleteOption } from '../InputAutocomplete/input-autocomplete.molecule';
import { InputAutocomplete } from '../InputAutocomplete/input-autocomplete.molecule';

interface ValidationRule<T> {
  message: string;
  value: T;
}
export interface ResponseAddressData {
  PlaceId: string;
  Place: {
    AddressNumber: string;
    Categories: string[];
    Country: string;
    Geometry: {
      Point: number[];
    };
    Interpolated: boolean;
    Label: string;
    // city
    Municipality: string;
    PostalCode: number;
    // state
    Region: string;
    Street: string;
    // county
    SubRegion: string;
    TimeZone: {
      Name: string;
      Offset: number;
    };
  };
}

interface Props {
  label: string;
  name: string;
  form: UseFormReturn<FieldValues>;
  dataCy?: string;
  rules?: {
    required?: ValidationRule<boolean>;
    maxLength?: ValidationRule<number>;
    minLength?: ValidationRule<number>;
    max?: ValidationRule<number>;
    min?: ValidationRule<number>;
    pattern?: ValidationRule<RegExp>;
    validate?:
      | Validate<string | number, FieldValues>
      | Record<string, Validate<string | number, FieldValues>>;
  };
  onChange: (selectedAddress: ResponseAddressData) => void;
  onBlur: () => void;
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  viewOnly?: boolean;
}

// This component is a wrapper around the InputAutocomplete component to handle the address search
export const InputAddress: React.FC<Props> = ({
  label,
  name,
  form,
  rules,
  dataCy = 'autocomplete-address',
  onChange,
  onBlur,
  defaultValue,
  disabled,
  readOnly,
  viewOnly
}) => {
  const [open, setOpen] = useState(false);
  const userApi = new UsersApi();

  const handleClose = () => {
    setOpen(false);
  };
  const [searchValue, setSearchValue] = useState('');
  const [searchText, debounceFns] = useDebounce(searchValue, 500);

  const [isLoading, setIsLoading] = useState(false);
  const [responseData, setResponseData] = useState<ResponseAddressData[]>([]);
  const [optionsData, setOptionsData] = useState<AutocompleteOption[]>([]);

  const getAddressData = async (searchText: string) => {
    setIsLoading(true);
    try {
      const response = await userApi.getAddress(searchText);
      const results = response.Results as ResponseAddressData[];
      const mappedOptions = results.map((item: ResponseAddressData) => {
        let label = ``;
        // label will normally be number and street but sometimes is just street with weird addresses
        if (item.Place?.AddressNumber) {
          label = `${item.Place?.AddressNumber} ${item.Place?.Street}`;
        }
        if (item.Place.Categories.includes('StreetType')) {
          label = `${item.Place?.Street}`;
        }
        return {
          label,
          value: item.PlaceId,
          optionLabel: item.Place?.Label
        };
      });
      setResponseData(results);
      setOptionsData(mappedOptions);
    } catch (error) {
      console.error('Error fetching address data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchText.length > 3) {
      setOpen(true);
      getAddressData(searchText);
    }
    return () => {
      debounceFns.cancel();
      setOptionsData([]);
    };
  }, [searchText]);

  return (
    <InputAutocomplete
      label={label}
      open={open}
      name={name}
      form={form}
      rules={rules}
      dataCy={dataCy}
      isLoading={isLoading}
      optionsData={optionsData}
      handleClose={handleClose}
      onSearchChange={setSearchValue}
      onParentChange={(selectedAddress: AutocompleteOption) => {
        const selectedOption = responseData.find(
          (option: ResponseAddressData) => option.PlaceId === selectedAddress.value
        );
        if (selectedOption) {
          onChange(selectedOption);
        }
      }}
      onParentBlur={() => {
        onBlur();
      }}
      optionIcon={<LocationIcon width="14px" height="14px" />}
      defaultValue={defaultValue}
      disabled={disabled}
      readOnly={readOnly}
      viewOnly={viewOnly}
    />
  );
};
